import { useEffect, useMemo, useState } from "react";
import "./App.css";

const FALLBACK_COPY = {
  generatedAt: null,
  edition: "Latest Edition",
  articles: [],
};

const FALLBACK_DATA = {
  markets: [
    { name: "S&P 500", value: "—", change: "—" },
    { name: "Dow Jones", value: "—", change: "—" },
    { name: "Nasdaq", value: "—", change: "—" },
  ],
  sports: [
    { matchup: "Yankees vs Red Sox", score: "—", status: "No score available" },
    { matchup: "Lakers vs Celtics", score: "—", status: "No score available" },
    { matchup: "Chiefs vs Bills", score: "—", status: "No score available" },
  ],
  sportsAll: [
    { matchup: "Yankees vs Red Sox", score: "—", status: "No score available" },
    { matchup: "Lakers vs Celtics", score: "—", status: "No score available" },
    { matchup: "Chiefs vs Bills", score: "—", status: "No score available" },
  ],
};

const SECTIONS = ["Top Stories", "U.S.", "World", "Business", "Tech", "Science", "Sports"];

const FALLBACK_SPONSOR = {
  generatedAt: null,
  activeSponsor: null,
  houseAd: {
    company_name: "Sponsor this edition",
    tagline: "Reach readers with one exclusive sponsor slot per day.",
    website_url: "mailto:theplainnews@icloud.com?subject=Sponsor%20The%20Plain%20News",
    logo_url: "",
    cta_text: "Email to sponsor a day",
    sponsor_copy: "The Plain News offers one exclusive sponsor slot each day across the website and email edition.",
    status: "house",
  },
};

function formatDate(value, options = {}) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    ...options,
  });
}

function normalizeArticles(payload) {
  if (!payload || typeof payload !== "object") return FALLBACK_COPY;
  const articles = Array.isArray(payload.articles) ? payload.articles : [];
  return {
    generatedAt: payload.generatedAt || null,
    edition: payload.edition || "Latest Edition",
    articles: articles.filter((article) => article && article.title && article.summary),
  };
}

function getEditionMeta(generatedAt, fallbackEdition) {
  const date = generatedAt ? new Date(generatedAt) : new Date();
  const hour = Number.isNaN(date.getTime()) ? new Date().getHours() : date.getHours();
  const edition = fallbackEdition || (hour < 12 ? "Morning Edition" : "Evening Edition");
  const nextEdition = hour < 12 ? "6:00 PM" : "6:00 AM";
  const freshness = formatDate(date, {
    weekday: "long",
    hour: "numeric",
    minute: "2-digit",
  });
  return { edition, dateLine: formatDate(date, { weekday: "long" }), nextEdition, freshness };
}

function normalizeCategory(category = "") {
  if (category === "Economics") return "Business";
  if (category === "Technology") return "Tech";
  return category || "Top Stories";
}

function SponsorBar({ sponsorConfig }) {
  const sponsor = sponsorConfig?.activeSponsor || sponsorConfig?.houseAd;
  if (!sponsor) return null;

  return (
    <section className="sponsor-bar">
      <div className="section-cap">Today's sponsor</div>
      <div className="sponsor-bar-inner">
        <div className="sponsor-copy">
          <div className="sponsor-name">{sponsor.company_name}</div>
          {sponsor.tagline ? <div className="sponsor-tagline">{sponsor.tagline}</div> : null}
          {sponsor.sponsor_copy ? <p>{sponsor.sponsor_copy}</p> : null}
        </div>
        <div className="sponsor-actions">
          {sponsor.logo_url ? <img src={sponsor.logo_url} alt={sponsor.company_name} className="sponsor-logo" /> : null}
          {sponsor.website_url ? (
            <a className="sponsor-button" href={sponsor.website_url} target="_blank" rel="noreferrer">
              {sponsor.cta_text || "Learn more"}
            </a>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Masthead({ edition, dateLine, activeSection, onSelectSection }) {
  return (
    <header className="masthead">
      <div className="nameplate-wrap">
        <img src="/plain-news-logo-tight.png" alt="The Plain News" className="logo-top" />
      </div>
      <div className="date-line">{dateLine} · {edition}</div>
      <div className="rule" />
      <nav className="section-nav" aria-label="Sections">
        {SECTIONS.map((section) => (
          <button key={section} className={section === activeSection ? "active" : ""} onClick={() => onSelectSection(section)}>
            {section}
          </button>
        ))}
      </nav>
      <div className="rule thin" />
    </header>
  );
}

function SourceLinks({ article, className = "story-sources" }) {
  if (!article?.link) return null;
  const label = article?.sources?.[0] || "Original source";
  return (
    <div className={className}>
      <a href={article.link} target="_blank" rel="noreferrer">Source: {label}</a>
    </div>
  );
}

function HeadlineList({ title, items }) {
  return (
    <section className="headline-list column-block">
      <div className="section-cap">{title}</div>
      {items.map((article) => (
        <article key={`${title}-${article.link || article.title}`} className="headline-item">
          <h3>{article.title}</h3>
          <p>{article.summary}</p>
          <SourceLinks article={article} />
        </article>
      ))}
    </section>
  );
}

function LeadStory({ article, sectionLabel }) {
  if (!article) return null;
  return (
    <article className="lead-story column-block">
      <div className="section-cap">{sectionLabel}</div>
      <div className="story-meta">{normalizeCategory(article.category)} · {formatDate(article.pubDate)}</div>
      <h1>{article.title}</h1>
      <p className="lead-dek">{article.summary}</p>
      <div className="lead-body">
        {(article.facts || []).slice(0, 4).map((fact) => (
          <p key={fact}>{fact}</p>
        ))}
      </div>
      <SourceLinks article={article} className="story-sources lead-sources" />
    </article>
  );
}

function EmailSignup() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmed = email.trim();

    if (!trimmed || !trimmed.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    try {
      setStatus("loading");
      setMessage("");

      const response = await fetch("/api/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed }),
      });

      const payload = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(payload?.error || `HTTP ${response.status}`);
      }

      setStatus(payload?.alreadySubscribed ? "success" : "success");
      setMessage(payload?.message || "Thanks — you are subscribed.");
      setEmail("");
    } catch (error) {
      console.error("Email signup failed", error);
      setStatus("error");
      setMessage(error.message || "Email signup is temporarily unavailable. Please try again.");
    }
  };

  return (
    <section className="signup-panel">
      <div className="section-cap">Email Edition</div>
      <h2>Get The Plain News by email.</h2>
      <p>
        A calm, plain-English digest you can read without doomscrolling.
      </p>
      <form className="signup-form" onSubmit={handleSubmit}>
        <input
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          aria-label="Email address"
          required
        />
        <button type="submit" disabled={status === "loading"}>
          {status === "loading" ? "Joining..." : "Join free"}
        </button>
      </form>
      {message ? <p className={`signup-message ${status}`}>{message}</p> : null}
    </section>
  );
}

function SupportBox() {
  return (
    <section className="support-panel">
      <div className="section-cap">Support</div>
      <h2>Support The Plain News.</h2>
      <p>
        If you value calm, factual reporting without clickbait or emotional manipulation, you can support the project.
      </p>
      <a
        className="support-button"
        href="https://buy.stripe.com/28EeV67Dkf1Ue5L0TM4Rq01"
        target="_blank"
        rel="noreferrer"
      >
        Support the project
      </a>
    </section>
  );
}

function DataBand({ data }) {
  const markets = data?.markets?.length ? data.markets : FALLBACK_DATA.markets;
  return (
    <section className="data-band single-column-band">
      <div className="data-column column-block">
        <div className="section-cap">Markets</div>
        {markets.map((row) => (
          <div key={row.name} className="data-row">
            <span>{row.name}</span>
            <span>{row.value}</span>
            <span>{row.change}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function SportsScoreboard({ data }) {
  const games = data?.sportsAll?.length ? data.sportsAll : (data?.sports?.length ? data.sports : FALLBACK_DATA.sportsAll);
  return (
    <section className="sports-scoreboard column-block">
      <div className="section-cap">Scores & Schedule</div>
      <h2>Sports scoreboard</h2>
      <div className="scoreboard-list">
        {games.map((game) => (
          <div key={`${game.matchup}-${game.status}`} className="scoreboard-row">
            <div className="scoreboard-matchup">{game.matchup}</div>
            <div className="scoreboard-score">{game.score}</div>
            <div className="scoreboard-status">{game.status}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EditionStatus({ edition, freshness, storyCount }) {
  return (
    <section className="edition-status">
      <div className="edition-status-block">
        <div className="section-cap">Current Edition</div>
        <strong>{edition}</strong>
      </div>
      <div className="edition-status-block">
        <div className="section-cap">Published</div>
        <strong>{freshness || "Latest available"}</strong>
      </div>
      <div className="edition-status-block">
        <div className="section-cap">Story Count</div>
        <strong>{storyCount}</strong>
      </div>
    </section>
  );
}

function StoryGrid({ articles, title = "More Stories" }) {
  return (
    <section className="more-stories">
      <div className="section-cap full-width">{title}</div>
      {articles.map((article) => (
        <article key={`more-${article.link || article.title}`} className="story-brief">
          <div className="story-meta">{normalizeCategory(article.category)}</div>
          <h3>{article.title}</h3>
          <p>{article.summary}</p>
          <SourceLinks article={article} />
        </article>
      ))}
    </section>
  );
}

export default function App() {
  const [payload, setPayload] = useState(FALLBACK_COPY);
  const [data, setData] = useState(FALLBACK_DATA);
  const [status, setStatus] = useState("loading");
  const [activeSection, setActiveSection] = useState("Top Stories");
  const [sponsorConfig, setSponsorConfig] = useState(FALLBACK_SPONSOR);

  useEffect(() => {
    let cancelled = false;
    async function loadAll() {
      try {
        const [articlesRes, dataRes, sponsorRes] = await Promise.all([
          fetch("/articles.json", { cache: "no-store" }),
          fetch("/data.json", { cache: "no-store" }).catch(() => null),
          fetch("/sponsor.json", { cache: "no-store" }).catch(() => null),
        ]);
        if (!articlesRes.ok) throw new Error(`HTTP ${articlesRes.status}`);
        const json = await articlesRes.json();
        const dataJson = dataRes && dataRes.ok ? await dataRes.json() : FALLBACK_DATA;
        const sponsorJson = sponsorRes && sponsorRes.ok ? await sponsorRes.json() : FALLBACK_SPONSOR;
        if (!cancelled) {
          setPayload(normalizeArticles(json));
          setData(dataJson || FALLBACK_DATA);
          setSponsorConfig(sponsorJson || FALLBACK_SPONSOR);
          setStatus("ready");
        }
      } catch (error) {
        console.error("Failed to load edition", error);
        if (!cancelled) {
          setPayload(FALLBACK_COPY);
          setData(FALLBACK_DATA);
          setSponsorConfig(FALLBACK_SPONSOR);
          setStatus("error");
        }
      }
    }
    loadAll();
    return () => { cancelled = true; };
  }, []);

  const articles = useMemo(() => payload.articles || [], [payload]);
  const { edition, dateLine, freshness } = getEditionMeta(payload.generatedAt, payload.edition);
  const sectionArticles = useMemo(() => {
    if (activeSection === "Top Stories") return articles;
    return articles.filter((article) => normalizeCategory(article.category) === activeSection);
  }, [articles, activeSection]);

  const lead = sectionArticles[0] || null;
  const leftColumn = sectionArticles.slice(1, 6);
  const rightColumn = sectionArticles.slice(6, 11);
  const moreStories = sectionArticles.slice(11, 27);

  return (
    <main className="newspaper-shell coherent-paper">
      <Masthead edition={edition} dateLine={dateLine} activeSection={activeSection} onSelectSection={setActiveSection} />

      <SponsorBar sponsorConfig={sponsorConfig} />

      <section className="front-page-intro">
        <p>
          The Plain News is a calm, twice-daily digital newspaper. It focuses on what happened, avoids emotional framing, and gives readers a finite edition instead of an endless feed.
        </p>
        <p className="front-page-subnote">
          Built to replace compulsive checking with a clean morning and evening read.
        </p>
      </section>

      <EditionStatus edition={edition} freshness={freshness} storyCount={articles.length} />

      <section className="top-grid balanced-grid">
        <HeadlineList title={activeSection === "Top Stories" ? "Headlines" : `${activeSection} Briefing`} items={leftColumn.length ? leftColumn : sectionArticles.slice(0, 3)} />
        {lead ? (
          <LeadStory article={lead} sectionLabel={activeSection === "Top Stories" ? "Top Story" : activeSection} />
        ) : (
          <article className="lead-story column-block">
            <div className="section-cap">{activeSection}</div>
            <h1>{status === "error" ? "The latest edition could not be loaded" : `No stories available in ${activeSection}`}</h1>
            <p className="lead-dek">This section will fill out as more edition content is added.</p>
          </article>
        )}
        <HeadlineList title={activeSection === "Top Stories" ? "Additional Reports" : `${activeSection} Reports`} items={rightColumn.length ? rightColumn : sectionArticles.slice(3, 6)} />
      </section>

      {activeSection === "Top Stories" && (
        <>
          <DataBand data={data} />
          <EmailSignup />
          <SupportBox />
        </>
      )}

      {activeSection === "Sports" ? (
        <SportsScoreboard data={data} />
      ) : null}

      <StoryGrid articles={moreStories.length ? moreStories : sectionArticles.slice(0, 10)} title={activeSection === "Top Stories" ? "More Stories" : `${activeSection} Archive`} />

      <footer className="site-footer">
        <div>Finite editions. Plain facts. Source-linked reporting.</div>
        <div>
          Sponsor a day: <a href="mailto:theplainnews@icloud.com?subject=Sponsor%20The%20Plain%20News">theplainnews@icloud.com</a>
        </div>
        <div>
          Merch: <a href="http://theplainnews.printify.me/" target="_blank" rel="noreferrer">theplainnews.printify.me</a>
        </div>
      </footer>
    </main>
  );
}
